import React, { useEffect, useState } from "react";
import { AlertTriangle, TrendingUp, TrendingDown, Loader2, Sparkles } from "lucide-react";
import { api } from "@/lib/api";

const inr = (minor, cur) => {
  const d = cur?.decimals ?? 2;
  const val = (minor || 0) / Math.pow(10, d);
  const parts = val.toFixed(d).split(".");
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return `${cur?.symbol || "₹"}${parts.join(".")}`;
};

/**
 * BudgetAlerts — flags expense categories trending N% higher than the same
 * days-count window in the previous calendar month. Rendered on the Life
 * Dashboard so overspend is spotted immediately.
 */
export default function BudgetAlerts({ threshold = 20 }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    api.get(`/erp/budget-alerts?threshold_pct=${threshold}`)
      .then(r => { if (alive) setData(r.data); })
      .catch(() => {})
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [threshold]);

  if (loading) return <div className="bg-white border border-stone-200 rounded-2xl p-6 text-center text-stone-500 text-sm"><Loader2 className="inline animate-spin mr-1" size={14} /> Loading budget checks…</div>;
  if (!data || !data.alerts.length) return null;

  const cur = data.currency;
  const alertsOnly = data.alerts.filter(a => a.is_alert);
  const nothingWrong = alertsOnly.length === 0;

  return (
    <section
      className={`rounded-2xl overflow-hidden border ${nothingWrong ? "bg-white border-stone-200" : "bg-gradient-to-br from-amber-50 to-white border-amber-300"}`}
      data-testid="budget-alerts"
    >
      <div className="px-5 py-3 border-b border-stone-100 flex flex-wrap items-center gap-3">
        <div className={`w-8 h-8 grid place-items-center rounded-xl shrink-0 ${nothingWrong ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-800"}`}>
          {nothingWrong ? <Sparkles size={15} /> : <AlertTriangle size={15} />}
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[11px] uppercase tracking-widest font-bold text-stone-500">Budget check</div>
          <div className="text-sm font-bold text-stone-900">
            {nothingWrong ? "Everything on track this month" : `${alertsOnly.length} ${alertsOnly.length === 1 ? "category" : "categories"} trending high`}
          </div>
        </div>
        <div className="text-[10px] text-stone-500 font-mono shrink-0">
          {data.this_period.label}<br />vs {data.last_period.label}
        </div>
      </div>

      <ul className="divide-y divide-stone-100" data-testid="budget-alerts-list">
        {data.alerts.slice(0, 5).map(a => {
          const isNew   = a.pct_change === null && a.is_alert;
          const isAlert = a.is_alert;
          const isDown  = a.delta_paise < 0;
          const tone =
            isAlert && a.severity === "high"   ? "text-red-700 bg-red-50" :
            isAlert                              ? "text-amber-800 bg-amber-50" :
            isDown                               ? "text-emerald-700" :
                                                   "text-stone-600";
          return (
            <li key={a.code} className="px-5 py-3 flex items-center gap-3" data-testid={`budget-alert-${a.code}`}>
              <div className={`w-8 h-8 grid place-items-center rounded-xl shrink-0 ${isAlert ? (a.severity === "high" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-800") : "bg-stone-100 text-stone-500"}`}>
                {isDown ? <TrendingDown size={14} /> : <TrendingUp size={14} />}
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-bold text-stone-900 truncate">{a.name}</div>
                <div className="text-[11px] text-stone-500 font-mono">
                  {inr(a.this_period_paise, cur)} this month{" "}
                  <span className="text-stone-400">·</span>{" "}
                  <span>{a.last_period_paise ? `${inr(a.last_period_paise, cur)} last` : <b>new spend</b>}</span>
                </div>
              </div>
              <div className={`text-sm font-mono font-black text-right px-2 py-1 rounded ${tone}`}>
                {isNew
                  ? <span className="text-[10px] uppercase tracking-widest">New!</span>
                  : a.pct_change === null
                    ? "—"
                    : <>{a.pct_change > 0 ? "+" : ""}{a.pct_change.toFixed(0)}%</>}
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
